import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import {
  EMPTY_CLUB_FILTERS,
  hasActiveFilters,
  type ClubFilters,
  type GameTypeFilterKey,
  type PlatformFilterKey,
} from '@/services/clubFilterService';
import type { StakesLevelKey } from '@/services/stakesLevelService';

interface ClubFiltersContextValue {
  filters: ClubFilters;
  toggleGameType: (key: GameTypeFilterKey) => void;
  togglePlatform: (key: PlatformFilterKey) => void;
  toggleStakes: (key: StakesLevelKey) => void;
  setSearchQuery: (query: string) => void;
  clearFilters: () => void;
  hasActiveFilters: boolean;
}

const ClubFiltersContext = createContext<ClubFiltersContextValue | null>(null);

function toggleFilterValue<T extends string>(values: T[], key: T): T[] {
  return values.includes(key) ? values.filter((value) => value !== key) : [...values, key];
}

export function ClubFiltersProvider({ children }: { children: ReactNode }) {
  const [filters, setFilters] = useState<ClubFilters>(EMPTY_CLUB_FILTERS);

  const toggleGameType = useCallback((key: GameTypeFilterKey) => {
    setFilters((current) => ({
      ...current,
      gameTypes: toggleFilterValue(current.gameTypes, key),
    }));
  }, []);

  const togglePlatform = useCallback((key: PlatformFilterKey) => {
    setFilters((current) => ({
      ...current,
      platforms: toggleFilterValue(current.platforms, key),
    }));
  }, []);

  const toggleStakes = useCallback((key: StakesLevelKey) => {
    setFilters((current) => ({
      ...current,
      stakes: toggleFilterValue(current.stakes, key),
    }));
  }, []);

  const setSearchQuery = useCallback((query: string) => {
    setFilters((current) => ({
      ...current,
      searchQuery: query,
    }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(EMPTY_CLUB_FILTERS);
  }, []);

  const value = useMemo(
    () => ({
      filters,
      toggleGameType,
      togglePlatform,
      toggleStakes,
      setSearchQuery,
      clearFilters,
      hasActiveFilters: hasActiveFilters(filters),
    }),
    [filters, toggleGameType, togglePlatform, toggleStakes, setSearchQuery, clearFilters],
  );

  return <ClubFiltersContext.Provider value={value}>{children}</ClubFiltersContext.Provider>;
}

export function useClubFilters(): ClubFiltersContextValue {
  const context = useContext(ClubFiltersContext);
  if (!context) {
    throw new Error('useClubFilters must be used within ClubFiltersProvider');
  }
  return context;
}
